import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaPrint,
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaBoxOpen,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const StoreIssueReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const primaryButtonClass = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white`;
  const secondaryButtonClass = "bg-gray-200 hover:bg-gray-300 text-gray-800";
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // LocalStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "8";
  const branchType = (localStorage.getItem("branchType") || "HO").toUpperCase();
  const userId = localStorage.getItem("userId") || "";
  const loginUserId = localStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
      "x-loginuserid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, userId, loginUserId]
  );

  // Form state
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [issueNo, setIssueNo] = useState("");
  const [store, setStore] = useState("");
  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");

  // Data state
  const [storeList, setStoreList] = useState([]);
  const [data, setData] = useState([]);

  // Fetch store dropdown
  useEffect(() => {
    setLoading(true);

    axios
      .get(
        `http://nissi-india.com/api/getStoresByBranchId_siRpt?branchID=${branchId}`,
        {
          headers: commonHeaders,
        }
      )
      .then((res) => {
        const stores = res.data?.stores || [];
        setStoreList(stores);
      })
      .catch((err) => {
        console.error("Dropdown fetch failed", err);
        toast.error("Failed to load stores");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [branchId, commonHeaders]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const handleSearch = () => {
    if (!fromDate || !toDate) {
      toast.warning("Please select both From Date and To Date");
      return;
    }

    setLoading(true);

    const params = {
      branchID: branchId,
      storeIssueNo: issueNo || "",
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
      store: store || "",
      itemname: itemName || "",
      itemcode: itemCode || "",
    };

    axios
      .get("http://nissi-india.com/api/storeIssueReport", {
        params,
        headers: commonHeaders,
      })
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

        setData(list);
        toast.success(`Loaded ${list.length} records`);
      })
      .catch((err) => {
        console.error("Store Issue fetch failed", err);
        toast.error("Failed to fetch store issue listing");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePrint = () => {
    const params = new URLSearchParams({
      branchID: branchId,
      storeIssueNo: issueNo || "",
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
      store: store || "",
      itemname: itemName || "",
      itemcode: itemCode || "",
    }).toString();

    window.open(`/inventory/StoreIssueReport_AllPrint?${params}`, "_blank");
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setIssueNo("");
    setStore("");
    setItemCode("");
    setItemName("");
    setData([]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="STORE ISSUE REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-xl shadow-sm border ${
          isDark
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold">Store Issue Report</h1>
            <p className="text-sm text-gray-500 mt-1">
              Track and manage all store issue transactions
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? primaryButtonClass : secondaryButtonClass
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${secondaryButtonClass}`}
            >
              <FaDownload className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div
            className={`rounded-lg p-5 mb-6 ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Search Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" /> Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium mb-2">
                  From Date *
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  To Date *
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Issue No
                </label>
                <input
                  type="text"
                  value={issueNo}
                  onChange={(e) => setIssueNo(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  placeholder="Enter issue number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Store</label>
                <select
                  value={store}
                  onChange={(e) => setStore(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                >
                  <option value="">All Stores</option>
                  {storeList.map((s) => (
                    <option key={s.fld_storeid} value={s.fld_storeid}>
                      {s.fld_storename}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Code
                </label>
                <input
                  type="text"
                  value={itemCode}
                  onChange={(e) => setItemCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  placeholder="Enter item code"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Name
                </label>
                <input
                  type="text"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  placeholder="Enter item name"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200">
              <button
                onClick={handleSearch}
                className={`flex items-center gap-2 ${primaryButtonClass} px-4 py-2 rounded transition-colors`}
              >
                <FaSearch className="w-4 h-4" />
                Search
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                <FaPrint className="w-4 h-4" />
                Print Report
              </button>
            </div>
          </div>
        )}

        {/* Results Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              Results {data.length > 0 && `(${data.length} records)`}
            </h2>

            {data.length > 0 && (
              <span className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : data.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      S.No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Issue No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Value
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {item.fld_storesissueno}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.fld_date}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(item.totalNew)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {item.fld_username}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() =>
                            window.open(
                              `/inventory/StoreIssueReport_Print?id=${item.fld_storesissueid}`,
                              "_blank"
                            )
                          }
                          className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
                          title="Print Report"
                        >
                          <FaPrint className="w-4 h-4" />
                          Print
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <FaBoxOpen className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No store issue records
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default StoreIssueReport;